import { NavLink, useLocation } from "react-router";
import {
  AlertTriangle,
  Bug,
  CalendarRange,
  ClipboardList,
  Database,
  FileCode2,
  GitPullRequestArrow,
  LayoutDashboard,
  ListTodo,
  ParkingSquare,
  Server,
  Settings2,
  SplitSquareHorizontal,
  Users,
  FolderKanban,
  Presentation,
  Gavel,
  Workflow,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
} from "@/components/ui/sidebar";
import { MODULOS, PERMISSION_ACTIONS } from "@/lib/constants";
import { Marca, MarcaIcone } from "@/components/shared/marca";
import { useAuth } from "@/lib/auth-context";
import { cn } from "@/lib/utils";

const ICONES: Record<string, LucideIcon> = {
  cronograma: CalendarRange,
  requisitos: ClipboardList,
  legados: Server,
  dados: Database,
  backlog: ListTodo,
  gaps: SplitSquareHorizontal,
  "parking-lot": ParkingSquare,
  "riscos-issues": AlertTriangle,
  "change-requests": GitPullRequestArrow,
  ricefws: FileCode2,
  configuracoes: Settings2,
  bugs: Bug,
  workshops: Presentation,
  "decisoes-chave": Gavel,
  dashboard: LayoutDashboard,
};

// Só o que existe. Entradas para ecrãs por construir dariam 404 e fariam a
// aplicação parecer partida — Catálogos entra quando for feito.
const ADMIN = [
  { slug: "admin/projetos", titulo: "Projetos", icone: FolderKanban },
  // `gestorVe`: o Gestor acede a Catálogos do seu projeto (Sprint VI). As
  // outras páginas da Administração continuam só para Admin/Master.
  { slug: "admin/catalogos", titulo: "Catálogos", icone: Workflow, gestorVe: true },
  { slug: "admin/usuarios", titulo: "Utilizadores", icone: Users },
];

// Vistas de um módulo (PL-001). O sufixo é relativo ao slug; "" é a grelha.
// Com o menu colapsado o `SidebarMenuSub` esconde-se sozinho, e o item-pai
// continua a levar à grelha.
const SUBMENUS: Record<string, { sufixo: string; titulo: string }[]> = {
  ricefws: [
    { sufixo: "", titulo: "Grade" },
    { sufixo: "/kanban", titulo: "Kanban" },
  ],
  configuracoes: [
    { sufixo: "", titulo: "Grade" },
    { sufixo: "/kanban", titulo: "Kanban" },
  ],
};

export function AppSidebar() {
  const { hasPermission, isAdmin, papel } = useAuth();

  // Admin/Master veem as três páginas; o Gestor vê só as marcadas `gestorVe`
  // (Catálogos); Consultor/Leitor não veem nenhuma (Sprint VI).
  const admin = isAdmin ? ADMIN : papel === "gestor" ? ADMIN.filter((a) => a.gestorVe) : [];

  const programa = MODULOS.filter(
    (m) => m.grupo === "programa" && hasPermission(m.slug, PERMISSION_ACTIONS.READ),
  );
  const analise = MODULOS.filter(
    (m) => m.grupo === "analise" && hasPermission(m.slug, PERMISSION_ACTIONS.READ),
  );

  return (
    // `collapsible="icon"` e não "offcanvas": as grelhas deste produto têm 40 a
    // 60 colunas e precisam de toda a largura horizontal disponível.
    <Sidebar collapsible="icon">
      {/* Cabeçalho BRANCO sobre o menu verde, como no exemplo do slide 6: o
          azul do "PM ONE" sobre o verde da marca ficaria ilegível. */}
      <SidebarHeader className="border-sidebar-border border-b bg-white px-3 py-4">
        {/* Duas formas da mesma marca: o lockup quando a barra está aberta, o
            losango quando colapsa para ícone. Trocar por CSS e não por estado
            evita um salto visual no primeiro render. */}
        <div className="flex items-center gap-2 group-data-[collapsible=icon]:justify-center">
          <MarcaIcone className="hidden group-data-[collapsible=icon]:block" />
          <Marca altura="h-7" className="group-data-[collapsible=icon]:hidden" />
        </div>
      </SidebarHeader>

      <SidebarContent>
        {programa.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-white/70">Programa</SidebarGroupLabel>
            <SidebarMenu>
              {programa.map((m) => (
                <ItemNav key={m.slug} slug={m.slug} titulo={m.titulo} icone={ICONES[m.slug]} />
              ))}
            </SidebarMenu>
          </SidebarGroup>
        )}

        {analise.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-white/70">Análise</SidebarGroupLabel>
            <SidebarMenu>
              {analise.map((m) => (
                <ItemNav key={m.slug} slug={m.slug} titulo={m.titulo} icone={ICONES[m.slug]} />
              ))}
            </SidebarMenu>
          </SidebarGroup>
        )}

        {admin.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-white/70">Administração</SidebarGroupLabel>
            <SidebarMenu>
              {admin.map((a) => (
                <ItemNav key={a.slug} slug={a.slug} titulo={a.titulo} icone={a.icone} />
              ))}
            </SidebarMenu>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarRail />
    </Sidebar>
  );
}

function ItemNav({
  slug,
  titulo,
  icone: Icone,
}: {
  slug: string;
  titulo: string;
  icone?: LucideIcon;
}) {
  // ⚠ O ativo passa por `isActive`, e não por uma classe do `NavLink`. Antes
  // era a classe: o `data-active` do `SidebarMenuButton` ficava sempre `false`,
  // e as regras do shadcn para o item ativo — incluindo as do menu colapsado —
  // nunca corriam.
  const { pathname } = useLocation();
  const ativo = pathname === `/${slug}` || pathname.startsWith(`/${slug}/`);

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        tooltip={titulo}
        isActive={ativo}
        className={cn(
          // O hover só nos inativos: com `hover:text-white` solto, passar o
          // rato no item ativo punha texto branco sobre o verde-claro.
          "text-white data-[active=false]:hover:bg-white/15 data-[active=false]:hover:text-white",
          // O traço vermelho do exemplo, por dentro: uma borda deslocaria o
          // ícone um píxel no item ativo, e o menu colapsado desalinharia.
          "data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground data-[active=true]:font-semibold data-[active=true]:shadow-[inset_3px_0_0_#F54927]",
        )}
      >
        <NavLink to={`/${slug}`}>
          {Icone ? <Icone className="size-4" /> : null}
          <span>{titulo}</span>
        </NavLink>
      </SidebarMenuButton>

      {SUBMENUS[slug] && (
        <SidebarMenuSub>
          {SUBMENUS[slug].map((s) => {
            const destino = `/${slug}${s.sufixo}`;
            const subAtivo = pathname === destino;
            return (
              <SidebarMenuSubItem key={s.sufixo}>
                <SidebarMenuSubButton
                  asChild
                  // ⚠ `data-active` só quando ativo. O componente escreve
                  // `data-active="false"`, e a variante `data-active:` do
                  // Tailwind 4 casa com a PRESENÇA do atributo — todos os
                  // subitens pareceriam ativos. As props vêm depois, e ganham.
                  data-active={subAtivo || undefined}
                  className={cn(
                    "text-white [&:not([data-active])]:hover:bg-white/15 [&:not([data-active])]:hover:text-white",
                    "data-active:bg-sidebar-accent data-active:text-sidebar-accent-foreground data-active:font-semibold data-active:shadow-[inset_3px_0_0_#F54927]",
                  )}
                >
                  <NavLink to={destino} end>
                    <span>{s.titulo}</span>
                  </NavLink>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            );
          })}
        </SidebarMenuSub>
      )}
    </SidebarMenuItem>
  );
}
